import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classService from '../../services/classService';
import { FOOTER_CLASS_LABELS_ES } from '../../config/classContentEs';
import { isSpanishLocale } from '../../config/classUiConfig';

const FOOTER_CLASS_LABELS = {
    'Child Development Associate (CDA)': 'CDA',
    'Child Development and Learning (CDL)': 'CDL',
    'Health and Safety Competency Training': 'Health & Safety',
    'American Red Cross Adult and Pediatric First Aid/CPR/AED': 'CPR / First Aid',
    'Orientation for New Family Child Care Providers': 'Orientation',
};

const reorderFooterClasses = (data) => {
    const reordered = [...data];

    const cdaIndex = reordered.findIndex((c) =>
        c.title.toLowerCase().includes('cda') ||
        c.title.toLowerCase().includes('child development associate')
    );
    if (cdaIndex !== -1 && cdaIndex !== 0) {
        reordered.unshift(reordered.splice(cdaIndex, 1)[0]);
    }

    const cprIndex = reordered.findIndex((c) =>
        c.title.toLowerCase().includes('cpr') ||
        c.title.toLowerCase().includes('first aid')
    );
    if (cprIndex !== -1 && cprIndex !== reordered.length - 1) {
        reordered.push(reordered.splice(cprIndex, 1)[0]);
    }

    return reordered;
};

const TECH_SUPPORT_EMAIL = 'deniseosoria04@gmail.com';

function Footer() {
    const { t, i18n } = useTranslation('common');
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const data = await classService.getAllClasses();
                setClasses(reorderFooterClasses(data));
            } catch (error) {
                console.error('Error fetching classes for footer:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchClasses();
    }, []);

    const footerClasses = useMemo(
        () => classes.filter((classItem) => FOOTER_CLASS_LABELS[classItem.title]),
        [classes]
    );

    const handleClassNavigation = (classId) => {
        window.scrollTo(0, 0);
        navigate(`/classes/${classId}`);
    };

    const getFooterLabel = (title) => {
        if (isSpanishLocale(i18n.language)) {
            return FOOTER_CLASS_LABELS_ES[title] || title;
        }
        return FOOTER_CLASS_LABELS[title] || title;
    };

    return (
        <footer className="w-full bg-white font-montserrat border-t border-gray-200 mt-12 sm:mt-16 lg:mt-24 pt-8 sm:pt-10 lg:pt-12 pb-4 text-[#222] text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between items-start px-4 sm:px-6 lg:px-8 gap-6 sm:gap-8">
                <div className="mb-6 lg:mb-0 flex flex-col items-center lg:items-start w-full lg:w-auto" style={{ minWidth: '220px' }}>
                    <img
                        src="/images/logo-img.png"
                        alt="YJ Child Care Plus Inc."
                        className="h-[80px] sm:h-[90px] lg:h-[100px] w-[240px] sm:w-[270px] lg:w-[300px] object-cover object-left mb-4"
                    />
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 w-full">
                    <div className="text-center lg:text-left">
                        <div className="text-[#979797] text-xs mb-3 uppercase tracking-widest">{t('footer.classes')}</div>
                        {loading ? (
                            <div className="text-[#222] text-sm">{t('loading')}</div>
                        ) : footerClasses.length === 0 ? (
                            <div className="text-[#222] text-sm">{t('noClassesAvailable')}</div>
                        ) : (
                            footerClasses.map((classItem) => (
                                <div key={classItem.id} className="mb-2">
                                    <button
                                        onClick={() => handleClassNavigation(classItem.id)}
                                        className="text-[#222] no-underline hover:underline text-sm bg-transparent border-none cursor-pointer p-0 text-left"
                                    >
                                        {getFooterLabel(classItem.title)}
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="text-center lg:text-left">
                        <div className="text-[#979797] text-xs mb-3 uppercase tracking-widest">{t('footer.contact')}</div>
                        <p className="text-[#222] text-sm mb-2">{t('footer.technicalHelp')}</p>
                        <a
                            href={`mailto:${TECH_SUPPORT_EMAIL}`}
                            className="text-[#222] no-underline hover:underline text-sm break-all"
                        >
                            {TECH_SUPPORT_EMAIL}
                        </a>
                    </div>
                </div>
            </div>
            <div className="w-full text-center text-[#979797] text-xs mt-6 sm:mt-8 pt-4 border-t border-gray-100 px-4">
                {t('footer.copyright')}
            </div>
        </footer>
    );
}

export default Footer; 